/**
 * Imperial CMS — i18n fundament.
 *
 * Self-rolled, type-safe lokalizacja oparta na TypeScript objects (`as const`).
 * Bez bibliotek (zero-deps) — wystarczająca dla MVP. W przyszłości można
 * przejść na `next-intl` gdy potrzebujemy ICU pluralization / formatery dat.
 *
 * Architektura — hybryda:
 *   - `lib/i18n/locales/{pl,en,de}.ts` — globalne strings (common buttons,
 *     errors, validation) wspólne dla całej aplikacji.
 *   - `app/{section}/i18n/{pl,en,de}.ts` — scoped strings per fragment
 *     aplikacji (setup wizard, admin top bar, ...).
 *   - `components/{kind}/{Name}/{Name}.i18n/{pl,en,de}.ts` — scoped per
 *     komponent CMS (catalog, window, layout).
 *
 * @see ../../.claude/CLAUDE.md sekcja "Internationalization (i18n)"
 */

/**
 * Recursive mapped type — preserves structure of `T` but unwraps string-literal
 * types to plain `string` (and keeps function types intact).
 *
 * Używany w komponentach z `.i18n/` folder'em — `pl.ts` deklaruje `as const`
 * + `export type FooTranslations = DeepString<typeof pl>`, dzięki czemu `en.ts`
 * / `de.ts` muszą zachować ten sam SHAPE (klucze + zagnieżdżenie) ale mogą
 * mieć WŁASNE wartości (bez `as const` byłoby `Type "Menu" is not assignable
 * to type "Menu"` — literal vs literal).
 *
 * @example
 *   // pl.ts
 *   export const pl = { hello: "Cześć" } as const;
 *   export type FooTranslations = DeepString<typeof pl>;
 *
 *   // en.ts
 *   import type { FooTranslations } from "./pl";
 *   export const en: FooTranslations = { hello: "Hello" }; // ✓ OK
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type DeepString<T> = {
  [K in keyof T]: T[K] extends string
    ? string
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    : T[K] extends (...args: any[]) => any
    ? T[K]
    : DeepString<T[K]>;
};

/** Lista wspieranych języków — single source of truth. */
export const LOCALES = ["pl", "en", "de"] as const;

/** Type unii dla wspieranych języków. */
export type Locale = (typeof LOCALES)[number];

/** Domyślny język — fallback gdy detection nie znajdzie match'a. */
export const DEFAULT_LOCALE: Locale = "pl";

/** Cookie name — czytane przez Server Components + edytowalne przez Server Actions. */
export const LOCALE_COOKIE = "imperial_cms_lang";

/**
 * Metadata locales — display name dla LangSelector.
 *
 * Flagi NIE są tutaj — są renderowane przez `<Flag locale={...} />` z
 * `lib/i18n/Flag.tsx` (inline SVG). Powód: emoji flag (🇵🇱 🇬🇧 🇩🇪) na Windows
 * nie renderują się jako kolorowe ikony — fallback do tekstu "PL"/"GB"/"DE".
 * SVG renderuje się identycznie na każdym OS/browserze.
 */
export const LOCALE_META: Record<Locale, { name: string; nativeName: string }> = {
  pl: { name: "Polish", nativeName: "Polski" },
  en: { name: "English", nativeName: "English" },
  de: { name: "German", nativeName: "Deutsch" },
};

/**
 * Type guard — sprawdza czy string jest valid Locale.
 * Używane gdy czytamy cookie/header (string nieuwfeny).
 */
export function isLocale(value: unknown): value is Locale {
  return typeof value === "string" && (LOCALES as readonly string[]).includes(value);
}

/**
 * Resolves translations dla danego locale + fallback.
 * Wymaga `Record<Locale, T>` w obrębie scope.
 *
 * @example
 *   import { setupTranslations } from "@/app/setup/i18n";
 *   const t = pickTranslations(setupTranslations, "en");
 *   t.step0.title // -> "Environment check"
 */
export function pickTranslations<T>(translations: Record<Locale, T>, locale: Locale): T {
  return translations[locale] ?? translations[DEFAULT_LOCALE];
}

/* ─── Visitor-facing messages — uniwersalne stringi dla całej aplikacji ─── */

import { messagesPl, type MessagesShape } from "./locales/pl";
import { messagesEn } from "./locales/en";
import { messagesDe } from "./locales/de";

/**
 * Mapa locale -> translations object. Używana przez `getMessages(locale)`.
 *
 * **Co tutaj jest:** visitor-facing stringi które pojawiają się NA STRONIE
 * publicznej (maintenance page, 404 title, homepage error). Stringi
 * admin-only (panel admina, dev banner) NIE są tutaj — admin UI nie jest
 * lokalizowane per-strona, używa polish jako jedyny język panel'u.
 *
 * **Konsumpcja w komponentach:** preferuj `getMessages(locale)` zamiast
 * bezpośrednich importów `messagesPl`/`messagesEn`/`messagesDe`. Helper
 * fallback'uje do default locale gdy nieznane locale.
 */
const ALL_MESSAGES: Record<Locale, MessagesShape> = {
  pl: messagesPl,
  en: messagesEn,
  de: messagesDe,
};

/**
 * Zwraca translations object dla podanego locale (fallback do `pl` gdy
 * locale nieobsługiwane). Type-safe — TypeScript wymusza istnienie wszystkich
 * kluczy w każdym locale przez `MessagesShape`.
 *
 * @example
 *   const t = getMessages("en");
 *   t.maintenance.title // -> "One moment"
 */
export function getMessages(locale: Locale): MessagesShape {
  return ALL_MESSAGES[locale] ?? ALL_MESSAGES[DEFAULT_LOCALE];
}

export type { MessagesShape };
